import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, useApiClient } from '../contexts/ApiContext';
import {
  ApiCreateBusinessRequest,
  ApiCurrentBusinessResponse,
  ApiUpdateProfileInfoRequest,
} from '../openapi';
import { mapResponse } from './helpers';

export function useBusinessQuery({ enabled = true } = {}) {
  const client = useApiClient();

  return useQuery<ApiCurrentBusinessResponse>({
    enabled: enabled,
    retry: true,
    throwOnError: true,
    queryKey: ['businesses', 'current'],
    refetchInterval: (query) => (query.state.data?.business.state === 'SURVEYED' ? 5000 : false),
    queryFn: ({ signal }) => client.GET('/businesses/current', { signal }).then(mapResponse),
  });
}

export function useAvailableBusinesses() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['businesses', 'available'],
    queryFn: ({ signal }) => client.GET('/businesses/available', { signal }).then(mapResponse),
  });
}

export function useUpdateProfileInfoMutation() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'profileInfo'],
    mutationFn: (body: ApiUpdateProfileInfoRequest) =>
      api
        .PATCH('/businesses/current/profileInfo', {
          body,
        })
        .then(() => null),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['businesses', 'current'],
      }),
  });
}

export function useProfileUsernameSuggestQuery(name: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['username', 'suggest', name],
    queryFn: () =>
      api
        .GET('/profiles/username/suggest', { params: { query: { name: btoa(name) } } })
        .then(mapResponse),
    enabled: name.length > 3,
  });
}

export function useCreateBusinessMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const { client, setBusinessId } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['businesses', 'create', idempotencyKey],
    mutationFn: async (body: ApiCreateBusinessRequest) => {
      const data = await client
        .POST('/businesses', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse);
      if (data) {
        await setBusinessId(data.profile.id);
      }

      queryClient.clear();

      return data;
    },
  });
}

export function useBusinessSettingsQuery() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['settings', 'customer'],
    queryFn: () => api.GET('/settings/customer').then(mapResponse),
    retry: true,
  });
}
